/* global VXConfig */

import React             from 'react';
import PropTypes         from 'prop-types';

import ActorTooFewItemsTile, {TYPES} from '../../Grid2/Tiles/ActorTooFewItemsTile';
import FormCheckbox                  from '../../Form/FormCheckbox';
import Grid2                         from '../../Grid2/Grid2';
import Headline                      from '../../SimpleElements/Headline';
import LogoLoader                    from '../../Loader/LogoLoader';
import Pagination                    from '../../Pagination';
import TagsCloud                     from '../../Video/Grid/Filter/TagsCloud';
import TextSearch                    from '../../Text/TextSearch';
import Sorter                        from '../../SimpleElements/Sorter';
import VideoTile                     from '../../Grid2/Tiles/VideoTile';

import {capitalizeFirstLetter, getUrlParams, setUrlParameters} from '../../../utils/CommonUtils';
import Flux                                                    from '../../../flux/Flux';
import {getSeoHeadlineTranslation}                             from '../../../utils/TranslationHelper';
import {isActorSeoTest}                                        from '../../Helper/SeoHelper';
import PjaxWrapper                                             from '../../../utils/PjaxWrapper';
import ReloadHelper                                            from '../../../utils/ReloadHelper';
import Translations                                            from '../../../utils/Translations';
import UrlBuilder                                              from '../../../utils/UrlBuilder';

const SEARCH_ORDERS = Flux.Constants.Vxql.Search.Order;

const PAGE_SIZE             = 16;
const PAGE_SIZE_VIDEO_ACTOR = 32;

const SORTING_OPTIONS = [
	{
		order:          SEARCH_ORDERS.NEWEST,
		text:           Translations.get('SortReleased'),
		title:          Translations.get('SortReleasedTitle'),
		withFreeFilter: true,
		withoutLogin:   true,
	},
	{
		order:          SEARCH_ORDERS.RATING,
		text:           Translations.get('SortRating'),
		title:          Translations.get('SortRatingTitle'),
		withFreeFilter: true,
		withoutLogin:   true,
	},
	{
		order:          SEARCH_ORDERS.LONGEST,
		text:           Translations.get('SortDuration'),
		title:          Translations.get('SortDurationTitle'),
		withFreeFilter: true,
		withoutLogin:   true,
	},
];

const URL_PARAM_NAME_SEARCH            = 'search';
const URL_PARAM_NAME_ORDER             = 'order';
const URL_PARAM_NAME_PAGE              = 'page';
const URL_PARAM_NAME_ONLY_FREE_CONTENT = 'onlyFreeContent';
const URL_PARAM_NAME_TAG               = 'tag';

const seoContent = {};

class ProfileVideosBlock extends React.PureComponent {
	static getPartialStateFromUrlParams() {
		const urlParams = getUrlParams(window.location.href);

		const {
			      [URL_PARAM_NAME_SEARCH]:            urlParamsSearchText,
			      [URL_PARAM_NAME_ORDER]:             urlParamsOrder,
			      [URL_PARAM_NAME_PAGE]:              urlParamsPage,
			      [URL_PARAM_NAME_ONLY_FREE_CONTENT]: urlParamsOnlyFreeContent,
			      [URL_PARAM_NAME_TAG]:               urlParamsTag,
		      }                   = urlParams;
		let currentPage           = 0;
		let sortIndex             = 0;
		let searchText            = '';
		let searchTag             = '';
		const showOnlyFreeContent = ['true', '1'].indexOf(urlParamsOnlyFreeContent) !== -1; // '1' for backwards compatibility

		if (typeof urlParamsPage === 'string') {
			const parsedPage = parseInt(urlParamsPage);

			if (!isNaN(parsedPage)) {
				currentPage = Math.max(parsedPage - 1, 0);
			}
		}

		if (typeof urlParamsOrder === 'string') {
			for (const [index, sortingOption] of SORTING_OPTIONS.entries()) {
				if (sortingOption.order === urlParamsOrder) {
					sortIndex = index;
				}
			}
		}

		if (typeof urlParamsSearchText === 'string') {
			searchText = urlParamsSearchText;
		}
		if (typeof urlParamsTag === 'string') {
			searchTag = urlParamsTag;
		}
		return {
			currentPage,
			sortIndex,
			searchText,
			showOnlyFreeContent,
			searchTag
		};
	}

	static getUrlParamsForHistoryState(historyState) {
		const urlParameters = {};
		if (historyState.searchText) {
			urlParameters[URL_PARAM_NAME_SEARCH] = historyState.searchText;
		}
		if (historyState.searchTag) {
			urlParameters[URL_PARAM_NAME_TAG] = historyState.searchTag;
		}
		if (historyState.currentPage > 0) {
			urlParameters[URL_PARAM_NAME_PAGE] = historyState.currentPage + 1;
		}
		if (historyState.showOnlyFreeContent) {
			urlParameters[URL_PARAM_NAME_ONLY_FREE_CONTENT] = !!historyState.showOnlyFreeContent;
		}
		if (historyState.sortIndex > 0 || Object.keys(urlParameters).length > 0) {
			urlParameters[URL_PARAM_NAME_ORDER] = SORTING_OPTIONS[historyState.sortIndex].order;
		}

		return setUrlParameters(window.location.href, urlParameters);
	}

	static getPageSize(isVideoActor) {
		return isVideoActor ? PAGE_SIZE_VIDEO_ACTOR : PAGE_SIZE;
	}

	static normalizeUrlForPjaxReloadComparison(url) {
		return setUrlParameters(url, {});
	}

	constructor(props) {
		super(props);

		const {currentPage, sortIndex, showOnlyFreeContent, searchText} = ProfileVideosBlock.getPartialStateFromUrlParams();

		this.state = {
			currentPage,
			sortIndex, // Relative to the SORTING_OPTIONS array, not to what is being displayed
			searchText,
			selectedTagGroup: 	   this.props.selectedTagGroup, // for VXMobile
			showOnlyFreeContent,
			filteredVideos:        null,
			filteredTotalCount:    0,
			unfilteredVideosCount: 0,
			tags:                  [],
			isLoading:             true,
		};

		this.isMobile = VXConfig.device.isMobile || Flux.Browser.getWindowSize().width < Flux.Constants.Grid2.BREAKPOINT_MIN_WIDTHS.sm;
		this.baseUrl  = ProfileVideosBlock.normalizeUrlForPjaxReloadComparison(window.location.href);

		this.onActorVideosChange         = this.onActorVideosChange.bind(this);
		this.onSearchTextChange          = this.onSearchTextChange.bind(this);
		this.onCurrentPageChange         = this.onCurrentPageChange.bind(this);
		this.onShowOnlyFreeContentChange = this.onShowOnlyFreeContentChange.bind(this);
		this.onSorterItemClick           = this.onSorterItemClick.bind(this);
		this.onWindowPopState            = this.onWindowPopState.bind(this);
		this.onBeforePjaxReload          = this.onBeforePjaxReload.bind(this);
		this.gotoSearchVideosByTag       = this.gotoSearchVideosByTag.bind(this);
		this.onSearchResult              = this.onSearchResult.bind(this);
		this.doSearch                    = this.doSearch.bind(this);
		this.onTagGroupToggle            = this.onTagGroupToggle.bind(this);
		this.getTagGroupLinks             = this.getTagGroupLinks.bind(this);
		this.onActorSeoContentChange     = this.onActorSeoContentChangeHandler.bind(this);
	}

	componentDidMount() {
		Flux.Search.addSearchResultListener(this.onSearchResult);
		Flux.Browser.addWindowPopStateListener(this.onWindowPopState);
		PjaxWrapper.addOnBeforeSendCallback(this.onBeforePjaxReload);
		Flux.Search.setCurrentSearchPattern('');
		this.props.searchTag ? Flux.Actor.loadActorSeoContent(this.props.actorId, this.props.searchTag) : null;
		Flux.Actor.addActorSeoContentChangeListener(this.onActorSeoContentChange);
		if(this.props.params.actorName === this.props.actorName.toLowerCase()){
			this.doSearch('');
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.searchTag && !seoContent[this.props.actorId] || this.props.searchTag && !seoContent[this.props.actorId][this.props.searchTag]) {
			Flux.Actor.loadActorSeoContent(this.props.actorId, this.props.searchTag);
		} else if (this.props.searchTag && seoContent[this.props.actorId] && seoContent[this.props.actorId][this.props.searchTag]) {
			this.updateSeoContent();
		}
		if(
			prevProps.isClassics !== this.props.isClassics 
			|| prevProps.actorId !== this.props.actorId 
			|| prevProps.searchTag !== this.props.searchTag
		) {
			this.doSearch();
		}
	}

	componentWillUnmount() {
		Flux.Search.removeSearchResultListener(this.onSearchResult);
		Flux.Search.setCurrentSearchPattern(null);
		Flux.Browser.removeWindowPopStateListener(this.onWindowPopState);
		PjaxWrapper.removeOnBeforeSendCallback(this.onBeforePjaxReload);
		Flux.Actor.removeActorSeoContentChangeListener(this.onActorSeoContentChange);
	}

	onBeforePjaxReload(opts) {
		// Check if we are switching to the videos block with different filters or if we are switching to a completely different page
		const toVideosBlockWithDifferentFilters = typeof opts.url === 'string' && ProfileVideosBlock.normalizeUrlForPjaxReloadComparison(opts.url) === this.baseUrl;
		// Returning false suppresses the pjax reload
		return !toVideosBlockWithDifferentFilters;
	}

	onActorVideosChange(actorId) {
		if (actorId === this.props.actorId) {
			this.doSearch();
		}
	}

	onSearchTextChange(searchText) {
		if (searchText !== this.state.searchText) {
			this.updateFilters({currentPage: 1, searchText});
		}
	}

	doSearch() {
		const pageSize = ProfileVideosBlock.getPageSize(this.props.isVideoActor);
		const filter   = {
			includedModelIds: [this.props.actorId], 
			sorting: SORTING_OPTIONS[this.state.sortIndex].order, 
			classic: this.props.isClassics
		};

		// searchTag for Desktop and selectedTagGroup for VXMobile
		if (this.props.searchTag || this.state.selectedTagGroup) {
			filter.tagGroup = this.props.searchTag || this.state.selectedTagGroup;
		}

		const offset = this.state.currentPage * pageSize;
		if (this.state.showOnlyFreeContent) {
			filter.free = true;
		}
		const searchString = this.state.searchText || '';
		Flux.Search.doSearch(searchString, Flux.Constants.SearchResults.Categories.videos.name, filter, offset, pageSize);
	}

	onSearchResult(result) {
		const filteredVideos = result.items.map(item => item.video);
		this.setState({
			filteredVideos,
			filteredTotalCount:    result.total,
			unfilteredVideosCount: 20,
			isLoading:             false,
		});
	}

	onCurrentPageChange(page) {
		this.updateFilters({currentPage: page});
	}

	onShowOnlyFreeContentChange(free) {
		this.updateFilters({currentPage: 0, free});
	}

	onSorterItemClick(item) {
		let sortIndex = 0;

		for (const [index, sortingOption] of SORTING_OPTIONS.entries()) {
			if (sortingOption.order === item._originalSortingOption.order) {
				sortIndex = index;
			}
		}

		if (sortIndex === this.state.sortIndex) {
			return;
		}


		this.updateFilters({currentPage: 1, sortIndex});
	}

	updateFilters({currentPage, sortIndex, free, searchText, pushHistoryState = true}) {
		let shouldPushHistoryState;

		this.setState((state) => {
			const newCurrentPage         = currentPage ? currentPage - 1 : state.currentPage;
			const newSortIndex           = typeof sortIndex === 'number' ? sortIndex : state.sortIndex;
			const newSearchText          = typeof searchText === 'string' ? searchText : state.searchText;
			const newShowOnlyFreeContent = typeof free === 'boolean' ? free : state.showOnlyFreeContent;


			shouldPushHistoryState = pushHistoryState && (
				newCurrentPage !== state.currentPage ||
				newSortIndex !== state.sortIndex ||
				newSearchText !== state.tags ||
				newShowOnlyFreeContent !== state.showOnlyFreeContent
			);

			return {
				showOnlyFreeContent: newShowOnlyFreeContent,
				currentPage:         newCurrentPage,
				searchText:          newSearchText,
				sortIndex:           newSortIndex,
			};
		}, () => {
			if (shouldPushHistoryState) {
				this.pushHistoryState();
				this.doSearch(this.state.newSearchText);

			}
		});
	}

	pushHistoryState(replace = false) {
		const historyState  = {
			...(window.history && window.history.state || {}), // Necessary for proper back-/forth-navigation
			pjax:                true,
			searchText:          this.state.searchText,
			searchTag:           this.props.searchTag,
			sortIndex:           this.state.sortIndex,
			currentPage:         this.state.currentPage,
			showOnlyFreeContent: this.state.showOnlyFreeContent,
		};
		const url           = ProfileVideosBlock.getUrlParamsForHistoryState(historyState);
		historyState.url    = url;
		const stateChangeFn = replace ? Flux.Browser.historyReplaceState : Flux.Browser.historyPushState;
		stateChangeFn(historyState, '', url);
	}

	onWindowPopState(event) {
		const {currentPage, sortIndex, searchText, showOnlyFreeContent} = event.state || {};

		if (currentPage !== undefined && sortIndex !== undefined && searchText !== undefined && showOnlyFreeContent !== undefined) {
			this.updateFilters({currentPage, sortIndex, searchText, free: showOnlyFreeContent, pushHistoryState: false});
		}
	}

	/**
	 * @param {Event} e
	 */
	gotoSearchVideosByTag(e) {
		const url = this.state.searchText.length > 0 ? UrlBuilder.buildVideoSearchUrl(this.state.searchText) : UrlBuilder.buildVideoAllRoute() + (this.props.searchTag ? "?tags=" + this.props.searchTag : '');
		return ReloadHelper.reload(url, e);
	}

	onTagGroupToggle(selectedTag, e) {
		const selectedTagGroup = decodeURIComponent(selectedTag.toLowerCase());
		const lastSelectedTag  = VXConfig.isVXMobilePlatform ? this.state.selectedTagGroup : this.props.searchTag?.toLowerCase();
		const nextTagGroup 	   = selectedTagGroup === lastSelectedTag ? '' : selectedTagGroup;
		const url              = (nextTagGroup 
			? UrlBuilder.buildActorProfileVideosTagGroupRoute(this.props.actorName.toLowerCase(), nextTagGroup) 
			: UrlBuilder.buildActorProfileVideosUrl(this.props.actorName)
		);
		if(this.props.navigateTo) {
			this.props.navigateTo(url, e, false);
		} else {
			const historyState = {...window.history && window.history.state};
			Flux.Browser.historyPushState(historyState, '', url);
		}
		const {currentPage, sortIndex, showOnlyFreeContent, searchText} = ProfileVideosBlock.getPartialStateFromUrlParams();
		this.setState({currentPage, sortIndex, showOnlyFreeContent, searchText, selectedTagGroup: nextTagGroup, isLoading: true}, () => {
			this.doSearch();
		});
	}

	getTagGroupLinks() {
		return this.props.availableTagGroups.map(tagGroup => ({
			tagGroup,
			link: UrlBuilder.buildActorProfileVideosTagGroupRoute(this.props.actorName.toLowerCase(), tagGroup)
		}));
	}

	onActorSeoContentChangeHandler() {
		const actorSeoContent = Flux.Actor.getActorSeoContent(this.props.actorId, this.props.searchTag);

		if (!seoContent[this.props.actorId]) {
			seoContent[this.props.actorId] = {};
		}

		seoContent[this.props.actorId][this.props.searchTag]  = actorSeoContent;
		this.updateSeoContent();
	}

	updateSeoContent() {
		document.title                                               = seoContent[this.props.actorId][this.props.searchTag].title;
		document.getElementsByTagName('meta')["description"].content = seoContent[this.props.actorId][this.props.searchTag].description;
	}

	render() {
		const isGuestLoggedIn = Flux.Guest.isLoggedIn();

		let videosGrid        = null;
		let paginationOptions = null;

		const pageSize = ProfileVideosBlock.getPageSize(this.props.isVideoActor);

		if (typeof this.state.filteredTotalCount === 'number' && this.state.filteredTotalCount > pageSize) {
			paginationOptions = {
				maxPage:     Math.ceil(this.state.filteredTotalCount / pageSize),
				currentPage: this.state.currentPage + 1,
			};
		}

		if (Array.isArray(this.state.filteredVideos)) {
			const tileData    = [...this.state.filteredVideos];
			const tileClasses = [];
			const tileSizes   = [];
			const tileConfigs = [];

			const videosCount = this.state.filteredTotalCount;

			if (videosCount < 4) {
				tileData.push({
					actorId:          this.props.actorId,
					actorName:        this.props.actorName,
					hasSearchResults: videosCount > 0,
				});
				tileClasses.push({index: videosCount, tileClass: ActorTooFewItemsTile});
				tileSizes.push({
					index: videosCount,
					size:  {xs: 2, lg: videosCount === 3 ? 4 : 4 - videosCount},
				});
				tileConfigs.push({
					index:  videosCount,
					config: {type: this.state.unfilteredVideosCount > 0 ? TYPES.ACTOR_VIDEO_SEARCH : TYPES.DEFAULT},
				});
			}

			videosGrid = (
				<div>
					{this.state.isLoading 
						? <LogoLoader pulse={true} isRelative={true}/>
						: <Grid2 
							columns		      = {{xs: videosCount === 0 ? 1 : 2, lg: videosCount === 0 ? 1 : 4}}
							defaultTileClass  = {VideoTile}
							defaultTileConfig = {{
								footer:     {show: {likes: true}},
								badges:     {textBadges: {show: {price: isGuestLoggedIn}}},
								navigateTo: this.props.navigateTo,
							}}
							headlineLimit = {5}
							headlineType  = {Flux.Constants.Headlines.h3}
							tileClasses   = {tileClasses}
							tileData      = {tileData}
							tileConfigs   = {tileConfigs}
							tileSizes     = {tileSizes}
						/>
					}

				</div>
			);
		} else {
			videosGrid = (
				<div className={'container-block--actor-grid__loader' + (paginationOptions ? ' -padding-top' : '')}>
					<LogoLoader className={'-static -size-150-percent'} pulse/>
				</div>
			);
		}

		const showFreeContentFilter = SORTING_OPTIONS[this.state.sortIndex].withFreeFilter;
		const availableSortOrders   = SORTING_OPTIONS
			.map((sortingOption, index) => ({
				text:                   sortingOption.text,
				selected:               this.state.sortIndex === index,
				_originalSortingOption: sortingOption, // Underscore so that Sorter continues working in the unlikely event if it interprets an originalSortingOption field in the future
			}))
			.filter(({
				         _originalSortingOption: {
					                                 withFreeFilter,
					                                 withoutLogin
				                                 }
			         }) => (withFreeFilter || !this.state.showOnlyFreeContent) && (withoutLogin || isGuestLoggedIn));

		const formCheckbox = (<FormCheckbox
			name="filterFreeContent"
			checked={this.state.showOnlyFreeContent}
			label={Translations.get('FilterOnlyFreeVideos')}
			onValueChange={this.onShowOnlyFreeContentChange}
		/>);

		const sorter = (<Sorter items={availableSortOrders}
		                        onItemClick={this.onSorterItemClick}
		                        className="sim-selector--sorting"
		                        label={Translations.get('SortBy')}
		                        hasLeftElement={true}
		                        hasRightElement={false}
		                        useSelectedIndexFromProps={true}
		                        isMobile={this.isMobile}
		                        additionalClasses={VXConfig.device.isTablet ? (isGuestLoggedIn ? " profile-videos-block__logged-in" : " profile-videos-block__logged-out") : ""}
		/>);

		let title;
		if (this.props.isClassics) {
			title = (
				<h2 className="context-box__title -classics" title={Translations.get('MediaPanelActorClassicsTooltip')}>
					{this.props.actorName}
					<img className="context-box__title_img" src="/assets/img/classics_logo_weiss.png"/>
				</h2>
			);
		} else {
			if (isActorSeoTest(this.props.actorId)) {
				let titleText;
				if (this.props.isClassics) {
					titleText = Translations.get('ClassicsTitle');
				} else {
					if (this.props.searchTag || VXConfig.routeName === 'Actor/ProfileVideos/TagGroup') {
						const tagGroup = this.props.searchTag || VXConfig.routeArgs['tagGroup'];
						titleText      = getSeoHeadlineTranslation('profile_videos_title_tag_h1', capitalizeFirstLetter(tagGroup), this.props.actorName);
					} else {
						titleText = getSeoHeadlineTranslation('profile_videos_title_h1', this.props.actorName);
					}
				}

				title = (
					<h1 className="context-box__title">
					<span className={"h-text-highlight"}>
						{titleText}
					</span>
					</h1>
				);
			} else {
				title = (

					<h2 className="context-box__title">
					<span className={"h-text-highlight"}>
						{this.props.isClassics ? Translations.get('ClassicsTitle') : SORTING_OPTIONS[this.state.sortIndex].title}
					</span>
					</h2>
				);
			}
		}

		if (this.props.isClassics && this.isMobile) {
			return (
				<div className={"container-block--actor-grid -margin-bottom-lg -classic-tab" + (this.isMobile ? ' h-pb-5' : '')}>
					<div className="media-panel container-block profile-videos-block--header">
						<div className={"actor-profile-block actor-profile--videos" + (this.props.isClassics ? ' -classics' : '')}>
						<Headline title={this.props.isClassics ? Translations.get('ClassicsTitle') : SORTING_OPTIONS[this.state.sortIndex].title} quantity={{number: this.state.filteredTotalCount}}>
							{sorter}
						</Headline>
							<div className="actor-profile--videos__grid">
								{this.isMobile && <div className={"profile-videos-block--header-controls-mobile"}>
								</div>}
								<div className="container-block--actor-grid__grid-container" id={'profile-videoalbums-container'}>
									{videosGrid}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={"container-block--actor-grid -margin-bottom-lg" + (this.isMobile ? ' h-pb-5 container-block--actor-grid--mood-4' : '')}>
				<div className="media-panel container-block profile-videos-block--header">
					{
						this.isMobile ?
						<Headline class={"vxheadline--less-padding"} title={this.props.isClassics ? Translations.get('ClassicsTitle') : SORTING_OPTIONS[this.state.sortIndex].title} quantity={{number: this.state.filteredTotalCount}}>
							{sorter}
						</Headline> :
						<div className={"context-box--actor-profile"}>
							{title}
						</div>
					}
					{this.props.isSeoProfileVideoFilter && <div className="video-grid__filter">
						<TagsCloud selected={[this.props.searchTag || this.state.selectedTagGroup]}
						           tags={this.props.availableTagGroups}
						           onToggle={this.onTagGroupToggle}
						           isDisabled={false}
								   navigateTo={this.props.navigateTo}
						           showLabel={false}
						           links={this.getTagGroupLinks()}
						/>
					</div>}

					<div className="profile-videos-block--header-controls">
						<TextSearch
							searchText={this.state.searchText}
							doSearch={this.onSearchTextChange}
							isMobile={this.isMobile}
							isMyVisitX={false}
							searchEmpty={true}
							lightTheme={this.isMobile}
						/>

						{this.isMobile && <div className={"profile-videos-block--header-controls-mobile"}>
							<div className={"profile-videos-block--header-controls-mobile-check"}>{formCheckbox}</div>
						</div>}

						{!this.isMobile && <div className="profile-videos-block--header-controls-right">
							{showFreeContentFilter &&
								<div id="filter-free-content-container" className="profile-videos-block--header-controls-right-item">
									{formCheckbox}
								</div>}
							<div className="profile-videos-block--header-controls-right-item -with-margin">
								<span
									className="profile-videos-block--header-controls-right-item-label -margin-right">{Translations.get('VideoGridFilterSorting')}</span>
								{sorter}
							</div>
						</div>}
					</div>
					<div className="container-block--actor-grid__grid-container" id={'profile-videoalbums-container'}>
						{videosGrid}
					</div>
				</div>

				{paginationOptions &&
					<div className={'col-xs-12 h-text-align-center'}>
						<Pagination maxPage={paginationOptions.maxPage}
						            goToFn={this.onCurrentPageChange}
						            currentPage={paginationOptions.currentPage}
						            isMobile={this.isMobile}
						            containerId={this.props.scrollToId}
						            inline={this.isMobile}
									scrollOnGoTo={true}
						/>
					</div>}
				{(!paginationOptions || (paginationOptions.maxPage === paginationOptions.currentPage)) &&
					<div className="profile-videos-block--pagination--search-all h-text-align-center">
					<span className="profile-videos-block--pagination--search-all-button" onClick={this.gotoSearchVideosByTag}>
						{Translations.get('SearchInAllModels')}
					</span>
					</div>}
			</div>
		);
	}
}

ProfileVideosBlock.propTypes = {
	actorId:                 PropTypes.number.isRequired,
	actorName:               PropTypes.string.isRequired,
	availableTagGroups:      PropTypes.array,
	isVideoActor:            PropTypes.bool,
	initialIsActorOnline:    PropTypes.bool.isRequired,
	params:				     PropTypes.object.isRequired,
	messagePrice:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	multiChatPrice:          PropTypes.number.isRequired,
	navigateTo:              PropTypes.func,
	isClassics:              PropTypes.bool,
	actorHasClassics:        PropTypes.bool,
	scrollToId:              PropTypes.string,
	searchTag:               PropTypes.string,
	selectedTagGroup:        PropTypes.string,
	isSeoProfileVideoFilter: PropTypes.bool,
};

ProfileVideosBlock.defaultProps = {
	isVideoActor:            false,
	isClassics:              false,
	actorHasClassics:        false,
	scrollToId:              'pjax-scrollto-pagination',
	selectedTagGroup:        '',
	isSeoProfileVideoFilter: false,
	availableTagGroups:      [],
};

export default ProfileVideosBlock;